import React, { useCallback, useEffect, useState } from 'react';
import {
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Slide,
} from '@mui/material';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import ErrorButton from '../Component/Buttons/ErrorButton';
import TextButton from '../Component/Buttons/TextButton';
import { NodeTextField } from '../NodeUI';

import './ConfirmDestructiveActionDialog.css';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function matchesConfirmPhrase(value, confirmPhrase, caseSensitive) {
  if (!confirmPhrase) return true;
  if (caseSensitive) return value === confirmPhrase;
  return value.trim().toLowerCase() === confirmPhrase.trim().toLowerCase();
}

export default function ConfirmDestructiveActionDialog({
  open = false,
  onClose,
  onConfirm,
  title,
  description,
  warning,
  headerDetail,
  confirmPhrase,
  requireCheckbox = false,
  checkboxLabel = 'I understand this action cannot be undone',
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  loading: externalLoading = false,
  confirmDisabled = false,
  children,
  maxWidth = 'sm',
  caseSensitive = true,
  manageLoading = true,
}) {
  const [confirmValue, setConfirmValue] = useState('');
  const [checked, setChecked] = useState(false);
  const [internalLoading, setInternalLoading] = useState(false);

  const loading = externalLoading || internalLoading;
  const needsTypedConfirm = Boolean(confirmPhrase);
  const typedConfirmMatches = matchesConfirmPhrase(confirmValue, confirmPhrase, caseSensitive);
  const checkboxOk = !requireCheckbox || checked;
  const canConfirm =
    !loading && !confirmDisabled && typedConfirmMatches && checkboxOk;

  const resetState = useCallback(() => {
    setConfirmValue('');
    setChecked(false);
    setInternalLoading(false);
  }, []);

  useEffect(() => {
    if (!open) resetState();
  }, [open, resetState]);

  const handleClose = () => {
    if (loading) return;
    resetState();
    onClose?.();
  };

  const handleConfirm = async () => {
    if (!canConfirm || !onConfirm) return;

    try {
      if (manageLoading) setInternalLoading(true);
      await onConfirm();
      resetState();
    } finally {
      if (manageLoading) setInternalLoading(false);
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth={maxWidth}
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      disableEnforceFocus
      PaperProps={{
        className: 'w86ConfirmDestructiveDialog',
        sx: {
          bgcolor: 'background.paper',
          color: 'text.primary',
        },
      }}
    >
      <DialogTitle component="div">
        <div className="w86ConfirmDestructiveDialog__titleRow">
          <WarningAmberOutlinedIcon className="w86ConfirmDestructiveDialog__icon" />
          <h2 className="w86ConfirmDestructiveDialog__title">{title}</h2>
        </div>
      </DialogTitle>

      <DialogContent>
        <div className="w86ConfirmDestructiveDialog__body">
          {headerDetail ? (
            <p className="w86ConfirmDestructiveDialog__headerDetail">{headerDetail}</p>
          ) : null}

          {description ? (
            <div className="w86ConfirmDestructiveDialog__description">{description}</div>
          ) : null}

          {warning ? (
            <div className="w86ConfirmDestructiveDialog__warning">{warning}</div>
          ) : null}

          {children}

          {needsTypedConfirm ? (
            <>
              <p className="w86ConfirmDestructiveDialog__confirmHint">
                Type <strong>{confirmPhrase}</strong> below to confirm.
              </p>
              <NodeTextField
                value={confirmValue}
                placeholder={`Type “${confirmPhrase}” to confirm`}
                onChange={setConfirmValue}
                disabled={loading}
                inputProps={{ autoComplete: 'off' }}
              />
            </>
          ) : null}

          {requireCheckbox ? (
            <FormControlLabel
              className="w86ConfirmDestructiveDialog__checkbox"
              control={
                <Checkbox
                  checked={checked}
                  onChange={event => setChecked(event.target.checked)}
                  disabled={loading}
                />
              }
              label={checkboxLabel}
            />
          ) : null}
        </div>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
        <TextButton variant="outlined" onClick={handleClose} disabled={loading}>
          {cancelLabel}
        </TextButton>
        <ErrorButton
          variant="contained"
          onClick={handleConfirm}
          disabled={!canConfirm}
          sx={{ position: 'relative', minWidth: 96 }}
        >
          {confirmLabel}
          {loading ? <CircularProgress size={24} className="buttonProgress" /> : null}
        </ErrorButton>
      </DialogActions>
    </Dialog>
  );
}
